// ============================================================================
// BrightPath - Notifications API Client
// BP-12-03: Notifications Frontend
// ============================================================================

import apiClient, { ApiResponse, PaginatedResponse } from '@/core/lib/api-client';
import type {
  Notification,
  NotificationListParams,
  NotificationListResponse,
  UnreadCountResponse,
  MarkReadRequest,
  MarkReadResponse,
  NotificationPreference,
  GlobalNotificationSettings,
  UpdatePreferencesRequest,
  UpdateGlobalSettingsRequest,
  SubscribePushRequest,
  PushSubscription,
  TestNotificationRequest,
} from '../types/notifications';

// ----------------------------------------------------------------------------
// API Endpoints
// ----------------------------------------------------------------------------

const NOTIFICATIONS_BASE = '/notifications';

// ----------------------------------------------------------------------------
// Notification CRUD Operations
// ----------------------------------------------------------------------------

/**
 * Fetch paginated list of notifications with optional filters
 */
export async function getNotifications(
  params: NotificationListParams = {}
): Promise<ApiResponse<NotificationListResponse>> {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('page_size', params.pageSize.toString());
  if (params.category) queryParams.append('category', params.category);
  if (params.isRead !== undefined) queryParams.append('is_read', params.isRead.toString());
  if (params.priority) queryParams.append('priority', params.priority);
  if (params.startDate) queryParams.append('start_date', params.startDate);
  if (params.endDate) queryParams.append('end_date', params.endDate);

  const query = queryParams.toString();
  const url = query ? `${NOTIFICATIONS_BASE}?${query}` : NOTIFICATIONS_BASE;
  
  return apiClient.get<NotificationListResponse>(url);
}

/**
 * Get a single notification by ID
 */
export async function getNotification(
  notificationId: string
): Promise<ApiResponse<Notification>> {
  return apiClient.get<Notification>(`${NOTIFICATIONS_BASE}/${notificationId}`);
}

/**
 * Delete a notification
 */
export async function deleteNotification(
  notificationId: string
): Promise<ApiResponse<void>> {
  return apiClient.delete<void>(`${NOTIFICATIONS_BASE}/${notificationId}`);
}

/**
 * Delete multiple notifications
 */
export async function deleteNotifications(
  notificationIds: string[]
): Promise<ApiResponse<{ deletedCount: number }>> {
  return apiClient.post<{ deletedCount: number }>(
    `${NOTIFICATIONS_BASE}/bulk-delete`,
    { notificationIds }
  );
}

// ----------------------------------------------------------------------------
// Read Status Operations
// ----------------------------------------------------------------------------

/**
 * Get unread notification count (total and by category)
 */
export async function getUnreadCount(): Promise<ApiResponse<UnreadCountResponse>> {
  return apiClient.get<UnreadCountResponse>(`${NOTIFICATIONS_BASE}/unread-count`);
}

/**
 * Mark a single notification as read
 */
export async function markAsRead(
  notificationId: string
): Promise<ApiResponse<Notification>> {
  return apiClient.patch<Notification>(
    `${NOTIFICATIONS_BASE}/${notificationId}/read`,
    {}
  );
}

/**
 * Mark multiple notifications as read
 */
export async function markMultipleAsRead(
  request: MarkReadRequest
): Promise<ApiResponse<MarkReadResponse>> {
  return apiClient.post<MarkReadResponse>(
    `${NOTIFICATIONS_BASE}/mark-read`,
    request
  );
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<ApiResponse<MarkReadResponse>> {
  return apiClient.post<MarkReadResponse>(
    `${NOTIFICATIONS_BASE}/mark-all-read`,
    {}
  );
}

// ----------------------------------------------------------------------------
// Notification Preferences
// ----------------------------------------------------------------------------

/**
 * Get all notification preferences for the current user
 */
export async function getPreferences(): Promise<ApiResponse<NotificationPreference[]>> {
  return apiClient.get<NotificationPreference[]>(`${NOTIFICATIONS_BASE}/preferences`);
}

/**
 * Get global notification settings
 */
export async function getGlobalSettings(): Promise<ApiResponse<GlobalNotificationSettings>> {
  return apiClient.get<GlobalNotificationSettings>(
    `${NOTIFICATIONS_BASE}/preferences/global`
  );
}

/**
 * Update preference for a specific category
 */
export async function updatePreference(
  request: UpdatePreferencesRequest
): Promise<ApiResponse<NotificationPreference>> {
  return apiClient.patch<NotificationPreference>(
    `${NOTIFICATIONS_BASE}/preferences/${request.category}`,
    request
  );
}

/**
 * Update global notification settings
 */
export async function updateGlobalSettings(
  request: UpdateGlobalSettingsRequest
): Promise<ApiResponse<GlobalNotificationSettings>> {
  return apiClient.patch<GlobalNotificationSettings>(
    `${NOTIFICATIONS_BASE}/preferences/global`,
    request
  );
}

// ----------------------------------------------------------------------------
// Push Notifications
// ----------------------------------------------------------------------------

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(
  request: SubscribePushRequest
): Promise<ApiResponse<PushSubscription>> {
  return apiClient.post<PushSubscription>(
    `${NOTIFICATIONS_BASE}/push/subscribe`,
    request
  );
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<ApiResponse<void>> {
  return apiClient.delete<void>(`${NOTIFICATIONS_BASE}/push/unsubscribe`);
}

/**
 * Get current push subscription status
 */
export async function getPushSubscriptionStatus(): Promise<
  ApiResponse<{ subscribed: boolean; subscription?: PushSubscription }>
> {
  return apiClient.get<{ subscribed: boolean; subscription?: PushSubscription }>(
    `${NOTIFICATIONS_BASE}/push/status`
  );
}

// ----------------------------------------------------------------------------
// Testing & Debug
// ----------------------------------------------------------------------------

/**
 * Send a test notification (for debugging/verification)
 */
export async function sendTestNotification(
  request: TestNotificationRequest
): Promise<ApiResponse<Notification>> {
  return apiClient.post<Notification>(
    `${NOTIFICATIONS_BASE}/test`,
    request
  );
}

// ----------------------------------------------------------------------------
// Export Grouped API
// ----------------------------------------------------------------------------

export const notificationsApi = {
  // CRUD
  list: getNotifications,
  get: getNotification,
  delete: deleteNotification,
  deleteMultiple: deleteNotifications,
  
  // Read status
  getUnreadCount,
  markAsRead,
  markMultipleAsRead,
  markAllAsRead,
  
  // Preferences
  getPreferences,
  getGlobalSettings,
  updatePreference,
  updateGlobalSettings,
  
  // Push
  subscribeToPush,
  unsubscribeFromPush,
  getPushSubscriptionStatus,
  
  // Testing
  sendTestNotification,
};

export default notificationsApi;

